/**
 * Frontend Recommendation Engine
 *
 * IMPORTANT: This file is the frontend counterpart to the backend recommendation engine.
 * Changes to recommendation logic MUST be synchronized with:
 *   - Backend: functions/src/backendRecommendationEngine.ts
 *
 * The frontend engine runs on manual user triggers.
 * The backend engine runs on scheduled triggers (Cloud Scheduler).
 * Both must produce consistent results.
 */
import {
  Portfolio,
  Position,
  WatchlistEntry,
  TradingRules,
  Recommendation,
  RecommendationScore
} from '../types';
import { TradingRulesService } from './tradingRulesService';
import { PortfolioService } from './portfolioService';
import { PositionService } from './positionService';
import { StockService } from './stockService';
import { scoringWeights, technicalFactors } from '../config/defaultTradingRules';
import { differenceInDays } from 'date-fns';

export class RecommendationEngine {
  /**
   * Main entry point - generate recommendations for all portfolios
   */
  static async generateRecommendations(): Promise<Recommendation[]> {
    const recommendations: Recommendation[] = [];
    
    try {
      console.log('=== RecommendationEngine: Starting generation ===');
      
      // Get all active portfolios
      const portfolios = await PortfolioService.getAllPortfolios();
      console.log(`Found ${portfolios.length} total portfolios`);
      
      const activePortfolios = portfolios.filter(p => p.isActive);
      console.log(`${activePortfolios.length} active portfolios to process`);
      
      // Generate recommendations for each portfolio
      for (const portfolio of activePortfolios) {
        console.log(`\nProcessing portfolio: ${portfolio.name} (${portfolio.id})`);
        const portfolioRecs = await this.generatePortfolioRecommendations(portfolio);
        console.log(`Generated ${portfolioRecs.length} recommendations for ${portfolio.name}`);
        recommendations.push(...portfolioRecs);
      }
      
      console.log(`\nTotal recommendations generated: ${recommendations.length}`);
      
      // Sort by urgency and score
      recommendations.sort((a, b) => {
        const urgencyOrder = { immediate: 0, high: 1, medium: 2, low: 3 };
        const urgencyDiff = urgencyOrder[a.urgency] - urgencyOrder[b.urgency];
        return urgencyDiff !== 0 ? urgencyDiff : b.score - a.score;
      });
      
      return recommendations;
    } catch (error) {
      console.error('Error generating recommendations:', error);
      throw error;
    }
  }

  /**
   * Generate recommendations for a specific portfolio
   */
  static async generatePortfolioRecommendations(
    portfolio: Portfolio
  ): Promise<Recommendation[]> {
    const recommendations: Recommendation[] = [];

    // Get trading rules for this portfolio (with version metadata for stamping)
    const { rules, rulesId, version } = await TradingRulesService.getEffectiveRulesWithVersion(portfolio.id);
    console.log(`Rules loaded for ${portfolio.name} (v${version}):`, {
      maxHoldingDays: rules.maxHoldingDays,
      defaultTrancheCount: rules.defaultTrancheCount,
      maxDistanceFromPivot: rules.maxDistanceFromPivot
    });

    // Get open positions (for exit/adjustment recommendations)
    const openPositions = await PositionService.getOpenPositions(portfolio.id);
    console.log(`Found ${openPositions.length} open positions`);

    // Get all active positions including pending (for capacity calculation)
    const activePositions = await PositionService.getActivePositions(portfolio.id);
    const pendingPositions = activePositions.filter(p => p.status === 'pending');
    console.log(`Found ${pendingPositions.length} pending positions (limit orders)`);

    // Get watchlist for technical data
    const watchlist = await StockService.getWatchlist();
    console.log(`Watchlist loaded with ${watchlist.length} stocks`);

    // 1. Check for position adjustments FIRST (before exits)
    // This ensures invalid targets/stops are flagged before exit logic runs
    console.log('Checking adjustment recommendations...');
    const adjustmentRecs = await this.generateAdjustmentRecommendations(openPositions, rules, watchlist);
    console.log(`Generated ${adjustmentRecs.length} adjustment recommendations`);
    recommendations.push(...adjustmentRecs);

    // 2. Check exit recommendations for existing positions (open only)
    // Note: Positions with pending adjustments should have those applied first
    console.log('Checking exit recommendations...');
    const exitRecs = await this.generateExitRecommendations(openPositions, rules);
    console.log(`Generated ${exitRecs.length} exit recommendations`);
    recommendations.push(...exitRecs);

    // 3. Generate entry recommendations if portfolio has capacity
    // Use portfolio's maxPositions if set, otherwise fall back to rules
    // Count both open AND pending positions against capacity
    const activePositionCount = activePositions.length;
    const maxPositions = portfolio.maxPositions || rules.defaultTrancheCount;
    console.log(`Portfolio capacity: ${activePositionCount}/${maxPositions} positions (${openPositions.length} open, ${pendingPositions.length} pending)`);

    // Calculate funds allocated to pending positions
    const pendingAllocation = pendingPositions.reduce((sum, p) => sum + (p.entryPrice * p.quantity), 0);
    const effectiveAvailableFunds = portfolio.availableFunds - pendingAllocation;
    console.log(`Funds: $${portfolio.availableFunds.toFixed(2)} available, $${pendingAllocation.toFixed(2)} in pending orders, $${effectiveAvailableFunds.toFixed(2)} effective`);

    if (activePositionCount < maxPositions && effectiveAvailableFunds > 0) {
      const availableSlots = maxPositions - activePositionCount;
      console.log(`Checking entry recommendations (${availableSlots} slots available, $${effectiveAvailableFunds.toFixed(2)} funds)...`);
      const entryRecs = await this.generateEntryRecommendations(
        { ...portfolio, availableFunds: effectiveAvailableFunds }, // Pass effective funds
        rules,
        activePositions, // Pass all active positions for duplicate checking
        availableSlots
      );
      console.log(`Generated ${entryRecs.length} entry recommendations`);
      recommendations.push(...entryRecs);
    } else {
      if (activePositionCount >= maxPositions) {
        console.log('Portfolio at max capacity - skipping entry recommendations');
      } else {
        console.log('No available funds for new positions - skipping entry recommendations');
      }
    }

    // Stamp all recommendations with the trading rules version
    for (const rec of recommendations) {
      rec.tradingRulesId = rulesId;
      rec.tradingRulesVersion = version;
    }

    return recommendations;
  }

  /**
   * Generate exit recommendations for open positions
   */
  private static async generateExitRecommendations(
    positions: Position[],
    rules: TradingRules
  ): Promise<Recommendation[]> {
    const recommendations: Recommendation[] = [];
    
    for (const position of positions) {
      const reasons: string[] = [];
      let urgency: Recommendation['urgency'] = 'low';
      let exitReason: Recommendation['exitReason'];
      let score = 0;
      
      // Calculate position metrics
      const daysHeld = position.holdingDays;
      const gainLossPercent = ((position.currentPrice - position.entryPrice) / position.entryPrice) * 100;
      
      // 1. Check stop loss
      // Sanity check: only trigger if stop loss is below entry (valid stop loss)
      if (position.stopLoss &&
          position.stopLoss < position.entryPrice &&
          position.currentPrice <= position.stopLoss) {
        reasons.push('Stop loss hit - exit immediately');
        urgency = 'immediate';
        exitReason = 'stop_hit';
        score = 100;
      }
      // 2. Check target price
      // CRITICAL: Only trigger "target reached" if target is ABOVE entry price
      // This prevents exiting at a loss due to misconfigured targets
      else if (position.targetPrice &&
               position.targetPrice > position.entryPrice &&
               position.currentPrice >= position.targetPrice) {
        reasons.push('Target price reached - take profits');
        urgency = 'high';
        exitReason = 'target_hit';
        score = 95;
      }
      // 3. Check max holding period
      else if (daysHeld >= rules.maxHoldingDays) {
        reasons.push(`Position held for ${daysHeld} days (max: ${rules.maxHoldingDays})`);
        urgency = 'high';
        exitReason = 'time_limit';
        score = 85;
        
        // If also stagnant, increase urgency
        if (Math.abs(gainLossPercent) < 3) {
          reasons.push('Price movement stagnant - rotate capital');
          exitReason = 'stagnant';
          score = 90;
        }
      }
      // 4. Check for stagnant positions (even if not at time limit)
      else if (daysHeld > rules.maxHoldingDays * 0.7 && Math.abs(gainLossPercent) < 2) {
        reasons.push(`Stagnant position: ${gainLossPercent.toFixed(1)}% change in ${daysHeld} days`);
        urgency = 'medium';
        exitReason = 'stagnant';
        score = 70;
      }
      
      // Create recommendation if any exit conditions met
      if (reasons.length > 0) {
        recommendations.push({
          id: `exit-${position.id}-${Date.now()}`,
          type: 'exit',
          action: 'sell',
          urgency,
          portfolioId: position.portfolioId,
          symbol: position.symbol,
          currentPrice: position.currentPrice,
          positionId: position.id,
          exitReason,
          score,
          reasons,
          technicalFactors: {
            momentum: gainLossPercent,
          },
          generatedAt: new Date(),
        });
      }
    }
    
    return recommendations;
  }

  /**
   * Generate position adjustment recommendations
   */
  private static async generateAdjustmentRecommendations(
    positions: Position[],
    rules: TradingRules,
    watchlist: WatchlistEntry[]
  ): Promise<Recommendation[]> {
    const recommendations: Recommendation[] = [];

    for (const position of positions) {
      const reasons: string[] = [];
      const gainPercent = ((position.currentPrice - position.entryPrice) / position.entryPrice) * 100;

      // Find watchlist entry for additional data (pivot price)
      const watchlistEntry = watchlist.find(w => w.symbol === position.symbol);
      const pivotPrice = watchlistEntry?.pivotPrice || position.entryPrice;

      // Debug: Log position stop/target values to diagnose detection issues
      console.log(`[Adjustment] ${position.symbol}: stopLoss=${position.stopLoss} (type: ${typeof position.stopLoss}), ` +
        `targetPrice=${position.targetPrice} (type: ${typeof position.targetPrice})`);

      // 0a. Check for MISSING stop loss - recommend setting one
      // Check for undefined, null, 0, or empty string
      const hasNoStopLoss = position.stopLoss === undefined || position.stopLoss === null || position.stopLoss === 0;
      if (hasNoStopLoss) {
        console.log(`[Adjustment] ${position.symbol}: Generating MISSING stop loss recommendation`);
        const recommendedStopLoss = position.entryPrice * (1 - (rules.stopLossPercentage / 100));
        const stopReasons = [
          `Position has no stop loss set - critical for risk management`,
          `Recommended stop loss at $${recommendedStopLoss.toFixed(2)} (${rules.stopLossPercentage}% below entry)`,
        ];

        recommendations.push({
          id: `adjust-stop-missing-${position.id}-${Date.now()}`,
          type: 'adjustment',
          action: 'adjust_stop',
          urgency: 'immediate',
          portfolioId: position.portfolioId,
          symbol: position.symbol,
          currentPrice: position.currentPrice,
          positionId: position.id,
          adjustmentType: 'raise_stop',
          adjustmentValue: recommendedStopLoss,
          score: 95,
          reasons: stopReasons,
          technicalFactors: {
            entryPrice: position.entryPrice,
            stopLossPercentage: rules.stopLossPercentage,
          },
          generatedAt: new Date(),
        });
      }

      // 0b. Check for MISSING target price - recommend setting one
      // Check for undefined, null, 0, or empty string
      const hasNoTargetPrice = position.targetPrice === undefined || position.targetPrice === null || position.targetPrice === 0;
      if (hasNoTargetPrice) {
        console.log(`[Adjustment] ${position.symbol}: Generating MISSING target price recommendation`);
        const recommendedTarget = this.calculateTargetPrice(position.entryPrice, pivotPrice, rules);
        const targetReasons = [
          `Position has no target price set - important for profit-taking strategy`,
          `Recommended target at $${recommendedTarget.toFixed(2)} based on trading rules`,
        ];

        recommendations.push({
          id: `adjust-target-missing-${position.id}-${Date.now()}`,
          type: 'adjustment',
          action: 'adjust_target',
          urgency: 'high',
          portfolioId: position.portfolioId,
          symbol: position.symbol,
          currentPrice: position.currentPrice,
          positionId: position.id,
          adjustmentType: 'raise_target',
          adjustmentValue: recommendedTarget,
          score: 90,
          reasons: targetReasons,
          technicalFactors: {
            entryPrice: position.entryPrice,
            pivotPrice,
          },
          generatedAt: new Date(),
        });
      }

      // 0c. Check for target price sanity (too low or too high)
      // This helps with manually entered positions or when rules are updated
      if (position.targetPrice) {
        const minTargetGainFromEntry = rules.minTargetGainFromEntry ?? 5;
        const maxTargetGainFromPivot = rules.maxTargetGainFromPivot ?? 25;

        const minTarget = position.entryPrice * (1 + minTargetGainFromEntry / 100);
        const maxTarget = pivotPrice * (1 + maxTargetGainFromPivot / 100);

        // Check if target is below minimum (below entry + min%)
        if (position.targetPrice < minTarget) {
          const targetReasons = [
            `Current target $${position.targetPrice.toFixed(2)} is only ${((position.targetPrice - position.entryPrice) / position.entryPrice * 100).toFixed(1)}% above entry`,
            `Target should be at least ${minTargetGainFromEntry}% above entry price of $${position.entryPrice.toFixed(2)}`,
          ];

          recommendations.push({
            id: `adjust-target-low-${position.id}-${Date.now()}`,
            type: 'adjustment',
            action: 'adjust_target',
            urgency: 'high',
            portfolioId: position.portfolioId,
            symbol: position.symbol,
            currentPrice: position.currentPrice,
            positionId: position.id,
            adjustmentType: 'raise_target',
            adjustmentValue: minTarget,
            score: 90,
            reasons: targetReasons,
            technicalFactors: {
              currentTarget: position.targetPrice,
              entryPrice: position.entryPrice,
              minTargetGainFromEntry,
            },
            generatedAt: new Date(),
          });
        }
        // Check if target is above maximum (above pivot + max%)
        else if (position.targetPrice > maxTarget && maxTarget > minTarget) {
          const targetReasons = [
            `Current target $${position.targetPrice.toFixed(2)} is ${((position.targetPrice - pivotPrice) / pivotPrice * 100).toFixed(1)}% above pivot`,
            `Target should be capped at ${maxTargetGainFromPivot}% above pivot price of $${pivotPrice.toFixed(2)}`,
          ];

          recommendations.push({
            id: `adjust-target-high-${position.id}-${Date.now()}`,
            type: 'adjustment',
            action: 'adjust_target',
            urgency: 'medium',
            portfolioId: position.portfolioId,
            symbol: position.symbol,
            currentPrice: position.currentPrice,
            positionId: position.id,
            adjustmentType: 'lower_target',
            adjustmentValue: maxTarget,
            score: 75,
            reasons: targetReasons,
            technicalFactors: {
              currentTarget: position.targetPrice,
              pivotPrice,
              maxTargetGainFromPivot,
            },
            generatedAt: new Date(),
          });
        }
      }

      // 1. Check for target price adjustment when position is performing very well
      const targetAdjustTrigger = rules.targetAdjustmentTrigger || 10;
      const targetThreshold = rules.targetAdjustmentThreshold || 10;
      
      if (position.targetPrice && gainPercent >= targetAdjustTrigger) {
        const targetDistance = ((position.targetPrice - position.currentPrice) / position.currentPrice) * 100;
        
        // If target is still more than threshold away and position already up significantly
        if (targetDistance > targetThreshold) {
          const reasons: string[] = [];
          
          // Calculate days held
          const daysHeld = position.entryDate ? 
            Math.floor((new Date().getTime() - new Date(position.entryDate).getTime()) / (1000 * 60 * 60 * 24)) : 0;
          
          reasons.push(`Position up ${gainPercent.toFixed(1)}% in ${daysHeld} days`);
          
          // Consider momentum and volume indicators
          if (watchlistEntry) {
            if (watchlistEntry.volumeChgVs50Day && watchlistEntry.volumeChgVs50Day < 0) {
              reasons.push('Volume declining - consider taking profits');
            }
            if (watchlistEntry.percentOffHigh && watchlistEntry.percentOffHigh > 5) {
              reasons.push(`Stock ${watchlistEntry.percentOffHigh.toFixed(1)}% off highs`);
            }
          }
          
          // Suggest a more conservative target
          const targetBuffer = rules.targetAdjustmentBuffer || 5;
          const suggestedTarget = position.currentPrice * (1 + targetBuffer / 100);
          
          // Only recommend if the new target is lower than current target
          if (suggestedTarget < position.targetPrice) {
            reasons.push(`Current target ${targetDistance.toFixed(1)}% away may be too aggressive`);
            
            recommendations.push({
              id: `adjust-target-${position.id}-${Date.now()}`,
              type: 'adjustment',
              action: 'adjust_target',
              urgency: 'medium',
              portfolioId: position.portfolioId,
              symbol: position.symbol,
              currentPrice: position.currentPrice,
              positionId: position.id,
              adjustmentType: 'lower_target',
              adjustmentValue: suggestedTarget,
              score: 80,
              reasons,
              technicalFactors: {
                momentum: gainPercent,
                volumeChange: watchlistEntry?.volumeChgVs50Day,
                percentOffHigh: watchlistEntry?.percentOffHigh,
              },
              generatedAt: new Date(),
            });
          }
        }
      }
      
      // 2. Check for trailing stop adjustment
      if (rules.trailingStopActivation && gainPercent >= rules.trailingStopActivation) {
        const currentStopLossPercent = ((position.entryPrice - position.stopLoss) / position.entryPrice) * 100;
        
        // Suggest raising stop to breakeven or higher
        if (currentStopLossPercent > 2) {
          const stopBuffer = rules.trailingStopBuffer || 1;
          const newStopLoss = position.entryPrice * (1 + stopBuffer / 100);
          
          if (newStopLoss > position.stopLoss) {
            const stopReasons = [`Position up ${gainPercent.toFixed(1)}% - raise stop to protect profits`];
            recommendations.push({
              id: `adjust-stop-${position.id}-${Date.now()}`,
              type: 'adjustment',
              action: 'adjust_stop',
              urgency: 'medium',
              portfolioId: position.portfolioId,
              symbol: position.symbol,
              currentPrice: position.currentPrice,
              positionId: position.id,
              adjustmentType: 'raise_stop',
              adjustmentValue: newStopLoss,
              score: 75,
              reasons: stopReasons,
              generatedAt: new Date(),
            });
          }
        }
      }
    }
    
    return recommendations;
  }

  /**
   * Generate entry recommendations
   */
  private static async generateEntryRecommendations(
    portfolio: Portfolio,
    rules: TradingRules,
    currentPositions: Position[],
    maxNewPositions: number
  ): Promise<Recommendation[]> {
    // Get watchlist
    const watchlist = await StockService.getWatchlist();
    console.log(`Watchlist has ${watchlist.length} stocks`);
    
    // Filter and score potential entries
    const candidates = await this.scoreEntryCandidates(
      watchlist,
      portfolio,
      rules,
      currentPositions
    );
    console.log(`Scored ${candidates.length} candidates`);
    
    // Take top candidates up to max positions
    const topCandidates = candidates
      .filter(c => c.totalScore >= rules.minEntryScore) // Use configurable score threshold
      .slice(0, maxNewPositions);
    
    console.log(`${topCandidates.length} candidates meet score threshold (≥${rules.minEntryScore})`);
    if (topCandidates.length > 0) {
      console.log('Top candidates:', topCandidates.map(c => ({
        symbol: c.symbol,
        score: c.totalScore.toFixed(1)
      })));
    }
    
    // Convert to recommendations
    const recommendations: Recommendation[] = topCandidates.map(candidate => {
      const watchlistEntry = watchlist.find(w => w.symbol === candidate.symbol)!;

      // Calculate position size based on portfolio's max positions
      const availableFunds = portfolio.availableFunds;
      const maxPositions = portfolio.maxPositions || rules.defaultTrancheCount;
      const remainingSlots = maxPositions - currentPositions.length;
      const positionSize = availableFunds / remainingSlots;
      const recommendedQuantity = Math.floor(positionSize / watchlistEntry.currentPrice);

      // For market orders, entry price is the current price (what you'll actually pay)
      const entryPrice = watchlistEntry.currentPrice;
      const pivotPrice = watchlistEntry.pivotPrice || entryPrice;

      // Calculate target price with sanity checks (based on entry price)
      const recommendedTarget = this.calculateTargetPrice(entryPrice, pivotPrice, rules);

      // Stop loss is calculated from pivot price per CANSLIM methodology
      const stopLossBase = watchlistEntry.pivotPrice || entryPrice;
      const recommendedStopLoss = stopLossBase * (1 - rules.stopLossPercentage / 100);

      return {
        id: `entry-${candidate.symbol}-${Date.now()}`,
        type: 'entry',
        action: 'buy',
        urgency: candidate.totalScore >= 85 ? 'high' : 'medium',
        portfolioId: portfolio.id,
        symbol: candidate.symbol,
        currentPrice: watchlistEntry.currentPrice,
        recommendedEntryPrice: entryPrice, // Market orders use current price
        recommendedQuantity,
        recommendedStopLoss,
        recommendedTarget,
        orderType: 'market', // Recommendations always use market orders for immediate execution
        score: candidate.totalScore,
        reasons: this.generateEntryReasons(candidate, watchlistEntry),
        technicalFactors: candidate.factors.reduce((acc, f) => {
          acc[f.name] = f.value;
          return acc;
        }, {} as any),
        generatedAt: new Date(),
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000), // 24 hours
      };
    });
    
    return recommendations;
  }

  /**
   * Score entry candidates
   */
  private static async scoreEntryCandidates(
    watchlist: WatchlistEntry[],
    portfolio: Portfolio,
    rules: TradingRules,
    currentPositions: Position[]
  ): Promise<RecommendationScore[]> {
    const scores: RecommendationScore[] = [];
    
    // Get recent trades to check cooldown
    const recentTrades = await PositionService.getPortfolioTransactions(portfolio.id);
    console.log(`Checking ${watchlist.length} watchlist entries against rules...`);
    
    let skippedNoPivot = 0;
    let skippedDistance = 0;
    let skippedVolume = 0;
    let skippedPriceChange = 0;
    let skippedTrackedPriceChange = 0;
    let skippedCooldown = 0;
    
    for (const entry of watchlist) {
      // Skip inactive stocks
      if (entry.isActive === false) {
        continue;
      }

      // Skip if we already have a position in this stock
      if (currentPositions.some((p) => p.symbol === entry.symbol)) {
        console.log(`${entry.symbol}: Skipped - already have position`);
        continue;
      }

      // Skip if no pivot price
      if (!entry.pivotPrice || entry.distanceFromPivot === null || entry.distanceFromPivot === undefined) {
        skippedNoPivot++;
        continue;
      }
      
      // Check basic entry criteria
      if (Math.abs(entry.distanceFromPivot) > rules.maxDistanceFromPivot) {
        skippedDistance++;
        console.log(`${entry.symbol}: Skipped - distance ${entry.distanceFromPivot.toFixed(2)}% > max ${rules.maxDistanceFromPivot}%`);
        continue;
      }
      // Volume check - must meet minimum volume increase threshold
      // Supports negative thresholds (e.g., -60 means stock needs at least 40% of average volume)
      if (entry.volumeChgVs50Day !== undefined && entry.volumeChgVs50Day < rules.minVolumeIncrease) {
        skippedVolume++;
        console.log(`${entry.symbol}: Skipped - volume ${entry.volumeChgVs50Day.toFixed(0)}% < min ${rules.minVolumeIncrease}%`);
        continue;
      }

      // Day price change filter - skip if price change doesn't meet minimum
      const minDayPriceChange = rules.minDayPriceChange ?? 0;
      if (minDayPriceChange !== 0 &&
          entry.priceChgPercent !== undefined &&
          entry.priceChgPercent < minDayPriceChange) {
        skippedPriceChange++;
        console.log(`${entry.symbol}: Skipped - price change ${entry.priceChgPercent.toFixed(2)}% < min ${minDayPriceChange}%`);
        continue;
      }

      // Tracked price change filter - skip if price change since tracking doesn't meet minimum
      const minTrackedPriceChange = rules.minTrackedPriceChange ?? 0;
      if (minTrackedPriceChange !== 0 && entry.priceWhenAdded !== undefined && entry.priceWhenAdded > 0) {
        const trackedPriceChange = ((entry.currentPrice - entry.priceWhenAdded) / entry.priceWhenAdded) * 100;
        if (trackedPriceChange < minTrackedPriceChange) {
          skippedTrackedPriceChange++;
          console.log(`${entry.symbol}: Skipped - tracked price change ${trackedPriceChange.toFixed(2)}% < min ${minTrackedPriceChange}%`);
          continue;
        }
      }

      // Check cooldown period
      const lastTrade = recentTrades
        .filter(t => t.symbol === entry.symbol)
        .sort((a, b) => b.executedAt.getTime() - a.executedAt.getTime())[0];
      
      if (lastTrade) {
        const daysSinceLastTrade = differenceInDays(new Date(), lastTrade.executedAt);
        if (daysSinceLastTrade < rules.minDaysBetweenSameStock) {
          skippedCooldown++;
          console.log(`${entry.symbol}: Skipped - cooldown ${daysSinceLastTrade} days < min ${rules.minDaysBetweenSameStock} days`);
          continue;
        }
      }
      
      // Calculate score
      const score = this.calculateEntryScore(entry, portfolio, rules, currentPositions);
      scores.push(score);
      console.log(`${entry.symbol}: Scored ${score.totalScore.toFixed(1)} (tech: ${score.components.technical.toFixed(1)}, fit: ${score.components.portfolioFit.toFixed(1)})`);
    }
    
    console.log('\nCandidate filtering summary:');
    console.log(`- No pivot price: ${skippedNoPivot}`);
    console.log(`- Distance too far: ${skippedDistance}`);
    console.log(`- Volume too low: ${skippedVolume}`);
    console.log(`- Day price change too low: ${skippedPriceChange}`);
    console.log(`- Tracked price change too low: ${skippedTrackedPriceChange}`);
    console.log(`- In cooldown: ${skippedCooldown}`);
    console.log(`- Scored: ${scores.length}`);
    
    // Sort by total score
    scores.sort((a, b) => b.totalScore - a.totalScore);
    
    return scores;
  }

  /**
   * Calculate entry score for a watchlist item
   */
  private static calculateEntryScore(
    entry: WatchlistEntry,
    portfolio: Portfolio,
    rules: TradingRules,
    currentPositions: Position[]
  ): RecommendationScore {
    const factors: RecommendationScore['factors'] = [];
    const components = { technical: 0, portfolioFit: 0, timing: 0, risk: 0 };
    
    // Technical Score (40%)
    const technicalScore = this.calculateTechnicalScore(entry, factors);
    components.technical = technicalScore * scoringWeights.entry.technical;
    
    // Portfolio Fit Score (30%)
    const portfolioFitScore = this.calculatePortfolioFitScore(entry, portfolio, currentPositions, factors);
    components.portfolioFit = portfolioFitScore * scoringWeights.entry.portfolioFit;
    
    // Timing Score (20%)
    const timingScore = this.calculateTimingScore(entry, factors);
    components.timing = timingScore * scoringWeights.entry.timing;
    
    // Risk Score (10%)
    const riskScore = this.calculateRiskScore(entry, factors);
    components.risk = riskScore * scoringWeights.entry.risk;
    
    // Calculate total
    const totalScore = Object.values(components).reduce((sum, val) => sum + val, 0);
    
    return {
      symbol: entry.symbol,
      totalScore,
      components,
      factors,
    };
  }

  /**
   * Calculate target price with sanity checks
   *
   * The target price is calculated based on the pivot price and targetGainPercentage,
   * but with important sanity checks:
   * 1. Target must be at least minTargetGainFromEntry % above the entry price
   * 2. Target is capped at maxTargetGainFromPivot % above the pivot price
   *
   * This handles cases where entry is significantly above pivot (target would be below entry)
   * and prevents unrealistic targets when entry is far above pivot.
   */
  private static calculateTargetPrice(
    entryPrice: number,
    pivotPrice: number,
    rules: TradingRules
  ): number {
    // Get rule parameters with defaults
    const targetGainPercent = rules.targetGainPercentage || 8;
    const minTargetGainFromEntry = rules.minTargetGainFromEntry ?? 5; // Default 5% minimum above entry
    const maxTargetGainFromPivot = rules.maxTargetGainFromPivot ?? 25; // Default 25% max above pivot

    // Calculate target based on pivot price and target gain percentage
    let target = pivotPrice * (1 + targetGainPercent / 100);

    // Sanity check 1: Target must be at least minTargetGainFromEntry % above entry price
    const minTargetFromEntry = entryPrice * (1 + minTargetGainFromEntry / 100);
    if (target < minTargetFromEntry) {
      console.log(`Target sanity check: Raising target from ${target.toFixed(2)} to ${minTargetFromEntry.toFixed(2)} ` +
        `(${minTargetGainFromEntry}% above entry ${entryPrice.toFixed(2)})`);
      target = minTargetFromEntry;
    }

    // Sanity check 2: Cap target at maxTargetGainFromPivot % above pivot
    const maxTargetFromPivot = pivotPrice * (1 + maxTargetGainFromPivot / 100);
    if (target > maxTargetFromPivot) {
      console.log(`Target cap: Lowering target from ${target.toFixed(2)} to ${maxTargetFromPivot.toFixed(2)} ` +
        `(${maxTargetGainFromPivot}% above pivot ${pivotPrice.toFixed(2)})`);
      target = maxTargetFromPivot;
    }

    return target;
  }

  /**
   * Calculate technical score component
   */
  private static calculateTechnicalScore(
    entry: WatchlistEntry,
    factors: RecommendationScore['factors']
  ): number {
    let score = 0;
    
    // Distance from pivot
    if (entry.distanceFromPivot !== null) {
      const distanceScore = Math.max(0, 100 - Math.abs(entry.distanceFromPivot) * 20);
      factors.push({
        name: 'distanceFromPivot',
        value: entry.distanceFromPivot,
        weight: technicalFactors.distanceFromPivot.weight,
        contribution: distanceScore * technicalFactors.distanceFromPivot.weight,
      });
      score += distanceScore * technicalFactors.distanceFromPivot.weight;
    }
    
    // Volume change
    if (entry.volumeChgVs50Day !== undefined) {
      const volumeScore = Math.min(100, entry.volumeChgVs50Day);
      factors.push({
        name: 'volumeChange',
        value: entry.volumeChgVs50Day,
        weight: technicalFactors.volumeChange.weight,
        contribution: volumeScore * technicalFactors.volumeChange.weight,
      });
      score += volumeScore * technicalFactors.volumeChange.weight;
    }
    
    // Base pattern
    if (entry.weeklyBaseType) {
      const isPreferred = technicalFactors.baseType.preferred.includes(entry.weeklyBaseType);
      const baseScore = isPreferred ? 100 : 70;
      factors.push({
        name: 'basePattern',
        value: baseScore,
        weight: technicalFactors.baseType.weight,
        contribution: baseScore * technicalFactors.baseType.weight,
      });
      score += baseScore * technicalFactors.baseType.weight;
    }
    
    return score;
  }

  /**
   * Calculate portfolio fit score
   */
  private static calculatePortfolioFitScore(
    entry: WatchlistEntry,
    portfolio: Portfolio,
    currentPositions: Position[],
    factors: RecommendationScore['factors']
  ): number {
    let score = 80; // Base score
    
    // Check sector diversification
    const currentSectors = currentPositions
      .map(p => {
        // Find watchlist entry to get industry
        const wEntry = this.watchlist?.find(w => w.symbol === p.symbol);
        return wEntry?.industryName;
      })
      .filter(Boolean);
    
    if (entry.industryName && currentSectors.includes(entry.industryName)) {
      score -= 20; // Penalize same sector
      factors.push({
        name: 'sectorDiversification',
        value: -20,
        weight: 0.5,
        contribution: -10,
      });
    } else {
      factors.push({
        name: 'sectorDiversification',
        value: 20,
        weight: 0.5,
        contribution: 10,
      });
    }
    
    return score;
  }

  /**
   * Calculate timing score
   */
  private static calculateTimingScore(
    entry: WatchlistEntry,
    factors: RecommendationScore['factors']
  ): number {
    let score = 70; // Base score
    
    // Bonus for stocks in early base stages
    if (entry.weeklyBaseStage === '1' || entry.weeklyBaseStage === '2') {
      score += 20;
      factors.push({
        name: 'baseStage',
        value: 20,
        weight: 0.5,
        contribution: 10,
      });
    }
    
    // Bonus for recent momentum
    if (entry.priceChgPercent && entry.priceChgPercent > 0) {
      score += 10;
      factors.push({
        name: 'momentum',
        value: entry.priceChgPercent,
        weight: 0.5,
        contribution: 5,
      });
    }
    
    return score;
  }

  /**
   * Calculate risk score
   */
  private static calculateRiskScore(
    entry: WatchlistEntry,
    factors: RecommendationScore['factors']
  ): number {
    let score = 80; // Base score
    
    // Check distance from high
    if (entry.percentOffHigh !== undefined) {
      if (entry.percentOffHigh > 15) {
        score -= 20; // Too far from highs
        factors.push({
          name: 'percentOffHigh',
          value: entry.percentOffHigh,
          weight: 1.0,
          contribution: -20,
        });
      }
    }
    
    return score;
  }

  /**
   * Generate human-readable reasons for entry
   */
  private static generateEntryReasons(
    score: RecommendationScore,
    entry: WatchlistEntry
  ): string[] {
    const reasons: string[] = [];
    
    // Add primary reason based on highest scoring factor
    const topFactor = score.factors.sort((a, b) => b.contribution - a.contribution)[0];
    
    if (topFactor.name === 'distanceFromPivot') {
      reasons.push(`Near pivot point (${entry.distanceFromPivot?.toFixed(1)}% away)`);
    }
    if (topFactor.name === 'volumeChange') {
      reasons.push(`Strong volume surge (${entry.volumeChgVs50Day?.toFixed(0)}% above average)`);
    }
    
    // Add pattern info
    if (entry.weeklyBaseType) {
      reasons.push(`${entry.weeklyBaseType} pattern in stage ${entry.weeklyBaseStage}`);
    }
    
    // Add score summary
    reasons.push(`Overall score: ${score.totalScore.toFixed(0)}/100`);
    
    return reasons;
  }

  // Cache for watchlist data
  private static watchlist: WatchlistEntry[] | null = null;
}